import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import "../App.css";
import { DraggableMessage } from "../components/Draggables";
import {
  COLOR_HIGHLIGHTED,
  FONT,
  REGEX_VALID_ROMAN,
  ROMAN_FONT,
  ROMAN_SYSTEM
} from "../defaults";
import { convertToAlternate, increaseProgress } from "../helpers";
import coin_1000_dez from "../images/coin_1000_dez.png";
import coin_100_dez from "../images/coin_100_dez.png";
import coin_10_dez from "../images/coin_10_dez.png";
import coin_1_dez from "../images/coin_1_dez.png";
import {
  buttonCheck,
  level1RomanTask1,
  level1RomanTask2,
  levelCorrect,
  levelIncorrect,
  levelNoInputMulti
} from "../strings";

const LevelNumber = 1;

function generateNumber(currentProgress) {
  const numbers = [17, 9, 134];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 998) + 1; // Generate integer between 1 and 999. These are optional but slightly harder, because the numbers are larger.
  }
  return numbers[currentProgress];
}

const containerStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  fontFamily: FONT,
  fontSize: "3dvh",
  padding: "0 3dvh",
  borderRadius: "0.25em",
  maxWidth: "40dvh",
  margin: "auto auto",
};

const containerArrayStyle = {
  display: "flex",
  flexDirection: "row",
  flexBasis: "0",
  flexWrap: "wrap",
  rowGap: "8dvh",
  justifyContent: "center",
  position: "absolute",
  top: "35%",
  transform: "translateY(-20%)",
  width: "100%",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  fontFamily: FONT,
  padding: "0",
  borderRadius: "0.25em",
  maxWidth: "100%",
  margin: "0 auto",
}

const inputStyle = {
  maxWidth: "10dvh",
  fontFamily: ROMAN_FONT,
  margin: "0 auto",
  marginTop: "3dvh",
};


const headerStyle = {
  padding: "40px",
  fontFamily: FONT,
  fontSize: "4dvh",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
};


/**
 * Returns HTML for all containers. Depending on isLast, sometimes returns plus-containers or equal-sign-containers and inputArray field.
 * @param {String} romanChar The Roman number letter which will be displayed as a coin.
 * @param {number} charCount How often romanChar occurs in the Roman string, i.e. in the Roman number.
 */
function renderCoinContainers(romanChar, charCount) {
  if (charCount === 0) { // If char is not present, we render nothing for it.
    return;
  }
  let howManyCoins = charCount;
  let coinImage = "";
  switch (romanChar) {
    case 'M':
      coinImage=coin_1000_dez;
      break;
    case 'D':
      coinImage=coin_100_dez;
      howManyCoins=5; // For D, L and V we set this to 5, so that we render the coins five times.
      break;
    case 'C':
      coinImage=coin_100_dez;
      break;
    case 'L':
      coinImage=coin_10_dez;
      howManyCoins=5; // For D, L and V we set this to 5, so that we render the coins five times.
      break;
    case 'X':
      coinImage=coin_10_dez;
      break;
    case 'V':
      coinImage=coin_1_dez;
      howManyCoins=5; // For D, L and V we set this to 5, so that we render the coins five times.
      break;
    case 'I':
      coinImage=coin_1_dez;
      break;
    default:
      console.log("Error renderContainers(): Invalid romanChar passed as argument!")
  }
  let coin = <span style={{width: "7.5dvh", height: "7.5dvh"}}><img src={coinImage} alt="coin" style={{ width: "100%", height: "100%", filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))",}}/></span>; // Use image instead of coin object.
  let container = [];
  for (let i = 0; i < howManyCoins; ++i) {
    container.push(coin);
  }
  return (
    <div>
      <div style={containerStyle}>
        {container}
      </div>
    </div>
  );
}

/**
 * Iterates through Roman string and renders all containers.
 * @param {number} number 
 * @param {String} inputArray 
 * @param {*} setInputArray 
 * @param {*} checkAnswers 
 * @param {boolean} disableInput 
 * @returns 
 */
function iterateString(number, inputArray, setInputArray, disableInput) {
  let romanString = convertToAlternate(ROMAN_SYSTEM, number);
  let containerArray = [];
  let usedChars = new Set();
  for (let i = 0; i < romanString.length; ++i) {
    let romanChar = romanString[i];
    if (usedChars.has(romanChar)) { // Skip char if we already processed it.
      continue;
    }
    usedChars.add(romanChar); // Remember that we processed this char now.
    let charCount = romanString.split(romanChar).length - 1; // Count how often this char occurs in the string.
    let inputIndex = 0; // Used to refer to the correct element of the input array; the element that corresponds to romanChar.
    switch (romanChar) { // Mapping romanChar to inputIndex.
      case "D": inputIndex = 0; break;
      case "C": inputIndex = 1; break;
      case "L": inputIndex = 2; break;
      case "X": inputIndex = 3; break;
      case "V": inputIndex = 4; break;
      case "I": inputIndex = 5; break;
      default: console.log("iterateString(): Invalid Roman char passed!")
    }
    containerArray.push(
    <div>
      {renderCoinContainers(romanChar, charCount)}
      <input 
      className="input-field" 
      style={inputStyle}
      maxLength={4}
      type="text"
      placeholder=""
      disabled={disableInput}
      value={inputArray[inputIndex]}
      pattern={REGEX_VALID_ROMAN}
      onChange={
        (e) => {
          e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
          const newInputArray = [...inputArray];
          newInputArray[inputIndex] = (e.target.value).toUpperCase();
          setInputArray(newInputArray);
        }
      }/>
    </div>
    );
  }
  return  <div>
            <div style={containerArrayStyle}>
            {containerArray}
            </div>
          </div>
  
  
}

/**
 * LLM-generated function. Turns a Roman string into an array of Roman characters.
 * @param {String} romanString 
 * @returns Array of Roman characters.
 */
function romanToArray(romanString) {
  // Define the Roman numerals in descending order of magnitude
  const romanUnits = ["D", "C", "L", "X", "V", "I"];
  
  // Initialize an object to store Roman numeral values
  const romanMap = { "D": "", "C": "", "L": "", "X": "", "V": "", "I": "" };
  
  // Iterate over each Roman numeral unit and check if it exists in the string
  for (const unit of romanUnits) {
      // If the unit exists in the string, store its value
      if (romanString.includes(unit)) {
          const regex = new RegExp(unit + "+", "g"); // Match consecutive units
          const match = romanString.match(regex);
          if (match) {
              romanMap[unit] = match[0];
              romanString = romanString.replace(match[0], ''); // Remove matched units from the string
          }
      }
  }
  
  // Return the array with the proper format
  return romanUnits.map(unit => romanMap[unit]);
}

function Level1Roman({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [number, setNumber] = useState(
    generateNumber(levelProgress[LevelNumber])
  );
  const [inputArray, setInputArray] = useState(Array.from({ length: 6 }, () => "")); // Array of length 6, each element initialized with empty string. Each element corresponds to a Roman number character.
  const [solutionArray, setSolutionArray] = useState(Array.from({ length: 6 }, () => "")); // Array of length 6, each element initialized with empty string. Each element corresponds to a Roman number character. Stores solution.
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);
  const [allFilled, setAllFilled] = useState(true); // Only set to false if checkAnswers was called with not all fields being filled.

  useEffect(() => {
    setNumber(generateNumber(levelProgress[LevelNumber]));
    setInputArray(Array.from({ length: 6 }, () => ""));
  }, [levelProgress]);

  useEffect(() => {
    setInputArray(Array.from({ length: 6 }, () => ""));
    const romanArray = romanToArray(convertToAlternate(ROMAN_SYSTEM, number));
    setSolutionArray(romanArray);
  }, [number]);

  const checkAnswers = () => {
    const romanString = convertToAlternate(ROMAN_SYSTEM, number);
    let totalNumberOfInputFields = new Set(romanString).size; // There are exactly as many input fields as there are unique characters in the Roman string.
    let filledInputFields = 0;
    inputArray.forEach((element) => {if (element !== "") ++filledInputFields}); // Counting number of non-empty input fields. Note that totalNumberOfInputFields - filledInputFields != emptyInputs because it does not account for input fields that are not shown.
    
    const inputString = inputArray.join(""); // Input array in string form, minus all the empty string elements.
    const isCorrect = (inputString === romanString);
    setCorrect(isCorrect);
    if (isCorrect) { // Correct Answer.
      setMessage(levelCorrect)
    } else if (filledInputFields < totalNumberOfInputFields) { // Not all input fields filled.
      setMessage(levelNoInputMulti);
      setAllFilled(false);
    } else { // Incorrect Answer.
      setMessage(levelIncorrect);
    }
    displayMessage();
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, ROMAN_SYSTEM);
      setInputArray(Array.from({ length: 6 }, () => ""));
    }
    if (allFilled) { // Only delete wrong inputs if all fields were filled.
      const newInputArray = [...inputArray];
      solutionArray.map((value, index) => {
        if (value !== inputArray[index]) newInputArray[index] = ""; // Remove wrong inputs.
      });
      setInputArray(newInputArray);
      setAllFilled(true);
    }
    setMessage("");
    setDisableInput(false);
    setCorrect(false);
  };

  return (
    <div>
      <div style={headerStyle}>
        {level1RomanTask1} <span style={{fontFamily: FONT, color: COLOR_HIGHLIGHTED, fontSize: "4dvh"}}>{number}</span> {level1RomanTask2}
      </div>
      <DndProvider options={HTML5toTouch}>
      {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
        <div>
          {iterateString(number, inputArray, setInputArray, disableInput)}
        </div>
      </DndProvider>
      <button className="checkAnswer-button" onClick={checkAnswers} disabled={disableInput}>
          {buttonCheck}
      </button>
    </div>
  );
}

export default Level1Roman;
